import React, { Fragment, useContext } from "react";
import { styled } from '@mui/material/styles';
import _ from "lodash";

import {List, ListItemText, ListItemAvatar, Box, ListItemButton, ListItem} from '@mui/material'
import MuiLink from '@mui/material/Link';

import { Link as GatsbyLink } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"

import { getEntityUrl } from "../util";
import { ListItemAssociatedEntity } from "./ListItemAssociatedEntity";

import { GlobalContext } from "../context/GlobalContext";
import {findListItemRole} from "../repository/EntityRepository";

const PREFIX = 'EntityList';

const classes = {
    root: `${PREFIX}-root`,
    inline: `${PREFIX}-inline`,
    image: `${PREFIX}-image`,
    listItem: `${PREFIX}-listitem`
};

const StyledList = styled(List)((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        backgroundColor: theme.palette.background.paper,
    },

    [`& .${classes.inline}`]: {
        display: 'inline',
    },

    [`& .${classes.listItem}`]: {
        borderBottom: '1px solid silver',
    },

    [`& .${classes.image}`]: {
        width: '150px',
        marginRight: '12px',
        verticalAlign: 'middle',
    }
}));

const ExhibitedInfo = styled('div')(() => ({
    fontSize: 'smaller',
    background: 'yellow',
}));

const Link = React.forwardRef(function Link(props, ref) {
    return <MuiLink component={GatsbyLink} ref={ref} {...props} />;
});

const EntityListItemLayout = ({ entity, title1, title2, title3, numCode, pageContext, sublistRole, image, associatedEntity, oaiRole, config }) => {
    const { defaultLocale } = pageContext;
    const sublistStyles = sublistRole && {
        ...(sublistRole?.backgroundColor ? {backgroundColor: sublistRole.backgroundColor + 'ac'} : {}),
    }
    const listType = config?.listType || 'standard'
    const displayOnlyTitles = listType === 'titleOnly'

    return (
        <Box sx={{ position: 'relative' }} className={"list-item " + classes.listItem} style={sublistStyles}>
            <Link to={getEntityUrl({ entity, defaultLocale })}
                  sx={{
                      textDecoration: 'none',
                      color: 'inherit',
                  }}
            >
                <ListItemButton>
                    {!displayOnlyTitles && (
                    <ListItemAvatar className={classes.image}>
                        {image && <GatsbyImage image={image} alt={title1} layout="fullWidth" />}
                        {oaiRole?.isExhibited === false &&
                            <ExhibitedInfo>Not exhibited</ExhibitedInfo>
                        }
                    </ListItemAvatar>
                    )}
                    <ListItemText primary={title1} secondary={<Fragment>{title2}</Fragment>} />
                </ListItemButton>
            </Link>
            {associatedEntity && associatedEntity.entity && <ListItemAssociatedEntity associatedEntity={associatedEntity} defaultLocale={defaultLocale} />}
        </Box>
    );
}

export const EntityListItem = ({ entity, pageContext, sublistRole, listItemLayout, config }) => {
    const listRole = findListItemRole(entity)
    const oaiRole = _.find(entity.Roles, { strapi_component: 'role.oai-config', syncIsExhibited: true });

    const title1 = listRole?.line1 || entity.title || '';
    const title2 = listRole?.line2 || false;
    const title3 = listRole?.line3 || false

    const image = entity.smallImage ? getImage(entity.smallImage.localFile) : null;
    const associatedEntity = listRole?.associatedEntity;
    const numCode = parseInt(listRole?.numCode || 0) || ""

    const ListItemLayout = listItemLayout || EntityListItemLayout;

    return (<ListItemLayout {...{entity, title1, title2, title3, numCode, pageContext, sublistRole, image, associatedEntity, oaiRole, config}} />)
};

const EntityInlineListItemLayout = ({ title1, title2, title3, sublistStyles, entity, listRole }) => {
    return (
        <ListItem className={"list-item " + classes.listItem} style={sublistStyles}>
            <ListItemText disableTypography className="inlinelist-headline"
                          primary={<h3 style={{ margin: 0, textTransform: 'uppercase' }} className="searchable">{title1}</h3>}
                          secondary={<h4 style={{ margin: 0, textTransform: 'uppercase', opacity: '60%' }} className="searchable">{title2 && title2}</h4>}
            />
        </ListItem>
    )
}

const InlineList = ({ entity, pageContext, listItemLayout, inlineListHeadlineLayout }) => {
    const listRole = findListItemRole(entity)
    const title1 = listRole?.line1 || entity.title || '';
    const title2 = listRole?.line2 || false;
    const title3 = listRole?.line3 || false;

    const sublistStyles = {
        ...(listRole?.backgroundColor ? {backgroundColor: listRole.backgroundColor} : {}),
        ...(listRole?.textColor ? {color: listRole.textColor} : {})
    }

    const InlineListItemLayout = inlineListHeadlineLayout || EntityInlineListItemLayout;

    return (
        <div className="inline-list">
            <InlineListItemLayout {...{title1, title2, title3, sublistStyles, entity, listRole}} />
            <EntityListItems entity={entity} pageContext={pageContext} sublistRole={listRole} listItemLayout={listItemLayout} inlineListHeadlineLayout={inlineListHeadlineLayout} />
        </div>
    )
}

export const EntityListItems = ({ entity, pageContext, sublistRole, listItemLayout, inlineListHeadlineLayout, config }) => {
    const { strapiChildren: children } = entity;
    let globalContext = useContext(GlobalContext);
    const { entityRepository } = globalContext;

    const renderInline = (listItemEntity) => {
        // we do not support nested inline lists due to the risk of endless loops
        return (!sublistRole && findListItemRole(listItemEntity, role => role.renderInline))
    }

    return (
        <>
            { children && children.map(listItemEntity => {
                const childEntityWithChildrenAndImage = entityRepository.findById(listItemEntity.id)
                if(childEntityWithChildrenAndImage) {
                    if (renderInline(listItemEntity)) {
                        return <InlineList key={childEntityWithChildrenAndImage.id} entity={childEntityWithChildrenAndImage}
                                           pageContext={pageContext} config={config} listItemLayout={listItemLayout}
                                           inlineListHeadlineLayout={inlineListHeadlineLayout}/>
                    } else {
                        // for language selection, the title is overridden by the language name before (see langRouter.js), so we reset it here
                        childEntityWithChildrenAndImage.title = listItemEntity.title
                        return <EntityListItem key={listItemEntity.id} entity={childEntityWithChildrenAndImage}
                                               pageContext={pageContext} config={config} sublistRole={sublistRole}
                                               listItemLayout={listItemLayout}/>;
                    }
                } else {
                    console.warn(`EntityListItems: invalid listItemEntity of entity`, {entity, childEntityWithChildrenAndImage, listItemEntity});
                }
            })}
        </>
    );
};

const EntityList = ({ entity, pageContext, config }) => {
    const listType = config?.listType || 'standard'

    return (
        <StyledList className={classes.root + " list-type-" + listType}>
            <EntityListItems entity={entity} pageContext={pageContext} config={config} listItemLayout={EntityListItemLayout} inlineListHeadlineLayout={EntityInlineListItemLayout}/>
        </StyledList>
    );
};

export default EntityList;
